import classnames from "classnames";
import React, { PureComponent, ReactNode } from "react";
import styled, { css } from "styled-components";

import theme from "../../../theme";
import Icon from "../../Icon";
import { IIconName } from "../../Icon/Icon.component";
import Tooltip from "../../Tooltip";
import FormErrorMessage from "../FormComponents/FormErrorMessage.component";

interface IInputWrapperProps {
  maxHeight?: string;
}

const InputWrapper = styled.div`
  display: flex;
  flex: 1;
  position: relative;
  min-height: 40px;
  max-height: ${({ maxHeight }: IInputWrapperProps): string =>
    maxHeight ? maxHeight : "40px"};
`;

const IconWrapper = styled.div`
  display: flex;
  position: absolute;
  right: 10px;
  align-self: center;

  @media screen and (max-width: 340px) {
    right: 5px;
  }
`;

const StyledIcon = styled(Icon)`
  & i {
    color: ${theme.colors.grey};
  }
`;

interface ITextProps {
  invalid: boolean;
}

const Text = styled.div<ITextProps>`
  color: ${(props: ITextProps): string =>
    props.invalid ? theme.colors.red : theme.colors.grey};
  font-size: 18px;
  letter-spacing: 0.8pt;
`;

interface IInputProps {
  invalid: boolean;
  required: boolean;
  hasPlaceholder?: boolean;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const inputComponentStyle = css<IInputProps>`
  flex: 1;
  flex-shrink: 0;
  min-width: 0;
  width: 100%;
  padding-left: 16.5px;
  padding-right: ${(props: IInputProps): string => {
    if (props.required || props.invalid) {
      return "21px";
    }

    return "16.5px";
  }};
  color: ${theme.colors.black};
  font-family: ${theme.fonts.regular};
  font-size: 15px;
  border: 1px solid ${theme.colors.brown};
  letter-spacing: 0.67px;
  outline: none;
  background-color: ${theme.colors.white};

  @media screen and (max-width: 340px) {
    padding-left: 8px;

    padding-right: "15px";
  }

  &.invalid {
    border-color: ${theme.colors.red};
    color: ${theme.colors.red};

    &::placeholder {
      color: ${theme.colors.red};
    }
  }
`;

const StyledInput = styled.input<IInputProps>`
  ${inputComponentStyle}

  &.disabled {
    background-color: ${theme.colors.lightGrey};
  }

  &::placeholder {
    letter-spacing: 0.67px;
    color: ${theme.colors.grey};
  }

  /* To remove native iOS style : */
  border-radius: 0;
  background-image: linear-gradient(transparent, transparent);

  /* Enable to use date input on mobile and desktop (with different UI) */
  &[type="date"] {
    &:not(.hasValue):before {
      color: ${theme.colors.grey};
      content: attr(placeholder);

      &.invalid {
        color: ${theme.colors.red};
      }
    }
    -webkit-appearance: none;

    &::-webkit-clear-button,
    &::-webkit-inner-spin-button,
    &::-webkit-calendar-picker-indicator {
      -webkit-appearance: none;
    }
    &::-webkit-datetime-edit {
      content: "";
      -webkit-appearance: none;
    }
  }
`;

const StyledDiv = styled.div<IInputProps>`
  ${inputComponentStyle}

  display:flex;
  align-items: center;
  ${(props: IInputProps): string | undefined => {
    if (props.hasPlaceholder) {
      return `color: ${theme.colors.grey};`;
    }
  }};
`;

const InfoIcon = styled(Icon)`
  i::before {
    background-color: ${theme.colors.beige};
    border-radius: 50%;
  }
`;

const StyledError = styled(FormErrorMessage)`
  margin-top: 4px;
`;

interface IProps {
  className?: string;
  disabled?: boolean;
  errorMessage?: string | null;
  hasInfoIcon?: boolean;
  iconName?: IIconName;
  infoChildren?: ReactNode;
  invalid?: boolean;
  maxHeight?: string;
  name?: string;
  onBlur?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  readonly?: boolean;
  required?: boolean;
  type?: string;
  value: string;
}

interface IState {
  openInfo: boolean;
}

class Input extends PureComponent<IProps, IState> {
  public static Input: typeof StyledInput = StyledInput;

  public state: IState = {
    openInfo: false,
  };

  public handleInfo = (): void => {
    this.setState((prevState: IState) => ({ openInfo: !prevState.openInfo }));
  };

  public render(): ReactNode {
    const {
      className,
      disabled,
      hasInfoIcon,
      iconName,
      infoChildren,
      invalid,
      maxHeight,
      name,
      onBlur,
      onChange,
      onFocus,
      placeholder,
      readonly,
      required,
      type,
      value,
    } = this.props;

    const InputComponent = !!readonly ? (
      <StyledDiv
        className={classnames({
          className,
          disabled,
          hasValue: !!value,
          invalid,
        })}
        required={!!required}
        invalid={!!invalid}
        hasPlaceholder={!value}
      >
        {value || placeholder}
      </StyledDiv>
    ) : (
      <StyledInput
        className={classnames({
          className,
          disabled,
          hasValue: !!value,
          invalid,
        })}
        disabled={disabled}
        type={type}
        value={value}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={onFocus}
        placeholder={placeholder}
        readOnly={!!readonly}
        required={!!required}
        invalid={!!invalid}
      />
    );

    return (
      <Wrapper>
        <InputWrapper maxHeight={maxHeight}>
          {InputComponent}
          <IconWrapper>
            {required && <Text invalid={!!invalid}>*</Text>}
            {iconName && <StyledIcon name={iconName} size={20} />}
            {hasInfoIcon && (
              <InfoIcon
                type="button"
                name="info-ok"
                size={20}
                onClick={this.handleInfo}
              />
            )}
          </IconWrapper>
        </InputWrapper>
        <StyledError message={this.props.errorMessage} />
        {this.state.openInfo && infoChildren && (
          <Tooltip onClick={this.handleInfo}>{infoChildren}</Tooltip>
        )}
      </Wrapper>
    );
  }
}

export default Input;
